import { Match, Show, Switch } from 'solid-js';
import { css } from '@emotion/css';
import { DateWrap, eventTaglineStyle, maxTextLines, nameStyle } from 'Shared/frontend/Poster';
import { ImageSchema,ImageSize } from 'Shared/model/basic';
import { poster, posterOfType } from 'Shared/frontend/SettingsProvider';
import { useProxyProductionImages } from 'Shared/common/ProxyProductionImagesProvider';
import { useUrls } from 'Shared/UrlProvider';


const inlineStyle = () => css({
	display: 'flex',
	flexDirection: 'column',
});

const inlineNameStyle = () => css(nameStyle(), {  
	margin: '0.5em 1em',
	...maxTextLines(2),
});


const inlineEventTaglineStyle = () => css(eventTaglineStyle(), {
	...maxTextLines(4),
	margin: '0.8em 1em',
});

const overlayedStyle = () => css({
	flexGrow: 1,
	backgroundSize: 'cover',
	backgroundPosition: 'center',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	padding: '1.5em 0 1em 0',
	textAlign: 'center',
});

const overlayedNameStyle = () => css(nameStyle(), {
	color: 'white',
	textShadow: 'black 1px 1px 3px',
	padding: '0 8px',
});	

const overlayedTaglineStyle = () => css(eventTaglineStyle(),{
	color: 'white',
	textShadow: 'black 1px 1px 3px',
	padding: '0 8px',
});

const squareStyle = () => css({
	position: 'relative',
	height: '330px',
});


const squareTextStyle = () => css({ 
	display: 'flex', 
	alignContent: 'center',
	padding: '2px',
	height: '33px',
	maxHeight: '33px',
	...maxTextLines(2),
});

const squareNameStyle = () => css(nameStyle(), squareTextStyle(), poster().photoPoster.squareName, { 
});


const squareTaglineStyle = () => css(eventTaglineStyle(), squareTextStyle(), poster().photoPoster.squareTagline,{
	
});

const squareImgStyle = () => css({
	width: '260px',
	height: '260px',
});



//XXX can *probably* share with menus and specials. No guarantee they'll be identical though.
export type PhotoPosterDimensions = 'portrait10x11' | 'landscape' | 'square' | 'portrait';

interface IPhotoPoster {
	image: ImageSchema,
	folder: string,
	size: ImageSize,
	imageDimensions: PhotoPosterDimensions
	name: string;
	photoPoster;
}

function showTagline(P)
{
	//WARNING 'tagline' and 'name' are used by specials as well as by events
	return P.tagline && ! P.posterSettings.hide?.includes('tagline');
}


export function PhotoPoster(P:IPhotoPoster)
{
	const showName = () => P.name && ! P.posterSettings.hide?.includes('name');

	return (
		<DateWrap {...P}>
			<Switch>
				{/* portait4x5 is being phased out no longer available for user to upload */}
				<Match when={P.imageDimensions == 'portrait10x11' || P.imageDimensions == 'portrait4x5'}>
					<OverlayedInfoPoster {...P} showName={showName()} />
				</Match>
				<Match when={P.imageDimensions == 'landscape'}>
					<InlineInfoPoster {...P} showName={showName()} />
				</Match>
				<Match when={P.imageDimensions == 'square'}>
					<SquareImagePoster {...P} showName={showName()} />
				</Match>

				{/* Used in Specials posters -- FIXME migrate existing to portrait4x5 and delete this case */}	
				<Match when={P.imageDimensions == 'portrait'}>
					<OverlayedInfoPoster {...P} showName={showName()} />
				</Match>
			</Switch>
		</DateWrap>
	);
}

function InlineInfoPoster(P:IPhotoPoster)
{
	const s = () => P.posterSettings;
	const theme = () => posterOfType('inlinePoster');
	const eventSize = () => s().nameFontSize ?? theme().name.fontSize;
	const taglineSize = () => s().taglineFontSize ?? theme().tagline.fontSize;

	return (
		<div class={inlineStyle()}>
			<div class={css(inlineNameStyle(),{fontSize:eventSize()})}>
				<Show when={P.showName} fallback={<>&nbsp;</>}>
					{P.name}
				</Show>
			</div>

			<img src={imageUrl(P)} />

			<Show when={showTagline(P)}>
				<div class={css(inlineEventTaglineStyle(), {fontSize:taglineSize()})}>
					{P.tagline}
				</div>
			</Show>
		</div>
	);
}

function SquareImagePoster(P:IPhotoPoster)
{
	const s = () => P.posterSettings;
	const theme = () => posterOfType('squareImagePoster');
	const eventSize = () => s().nameFontSize ?? theme().name.fontSize;
	const taglineSize = () => s().taglineFontSize ?? theme().tagline.fontSize;

	return (
		<div class={squareStyle()}>
			<Show when={P.showName}>
				<div class={css(squareNameStyle(),{fontSize:eventSize()})}>
					{P.name}
				</div>
			</Show>

			<img class={squareImgStyle()} src={imageUrl(P)} />

			<Show when={showTagline(P)}>
				<div class={css(squareTaglineStyle(), {fontSize: taglineSize()})} >
					{P.tagline}
				</div>
			</Show>
		</div>
	);
}

function OverlayedInfoPoster(P:IPhotoPoster)
{
	const s = () => P.posterSettings;
	const theme = () => posterOfType('photoPoster');
	const eventSize = () => s().nameFontSize ?? theme().name.fontSize;
	const taglineSize = () => s().taglineFontSize ?? theme().tagline.fontSize;

	return (
		<div class={overlayedStyle()} style={`background-image:url('${imageUrl(P)}')`}>
			<Show when={P.showName} fallback={<>&nbsp;</>}>
				<div class={css(overlayedNameStyle(),{fontSize:eventSize()})}>
					{P.name}
				</div>
			</Show>

			<Show when={showTagline(P)}>
				<div class={css(overlayedTaglineStyle(), {fontSize: taglineSize()})}>
					{P.tagline}
				</div>
			</Show>
		</div>
	);
}

function imageUrl(P:IPhotoPoster)
{
	if (!P.image?.hash) 
		return '';
	return useUrls().createVenueImageUrl(P.image.hash,P.image,P.folder,P.size,useProxyProductionImages());
}

